/**
 * FilesPanel Component
 * Main panel for managing files associated with an adventure
 */

import { useState, useCallback, useRef, useMemo } from 'react'
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  Select,
  Pagination,
  Alert,
  Stack,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@mui/material'
import {
  Search,
  GridView,
  ViewList,
  CloudUpload,
  Add,
  Delete,
  Info,
  Link as LinkIcon,
} from '@mui/icons-material'
import { useAdventureEditorStore } from '../../../stores/adventureEditorStore'
import { usePublishedPermissions } from '../../../hooks/usePublishedPermissions'
import {
  useAdventureFiles,
  useUploadAdventureFile,
  useDeleteAdventureFile,
} from '../../../hooks/useAdventureFiles'
import { AdventureFileCard, AdventureFileCardSkeleton } from '../media/AdventureFileCard'
import { AdventureFileListView } from '../media/AdventureFileListView'
import { AdventureFileDetailsDialog } from '../media/AdventureFileDetailsDialog'
import { AdventureFileUsagesDialog } from '../media/AdventureFileUsagesDialog'
import type { AdventureMediaFileDto, GetAdventureFilesParams } from '@api/types/adventureFiles'

type ViewMode = 'grid' | 'list'
type MediaTypeFilter = 'all' | 'image' | 'video' | 'audio' | 'document'
type SortBy = 'name' | 'date' | 'size'

const PAGE_SIZE = 20

export function FilesPanel() {
  const adventureId = useAdventureEditorStore((state) => state.adventureId)

  // Published permissions
  const { isPublished, canEditImages } = usePublishedPermissions()
  const canModify = !isPublished || canEditImages

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [mediaType, setMediaType] = useState<MediaTypeFilter>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<SortBy>('date')
  const [sortDesc, setSortDesc] = useState(true)
  const [page, setPage] = useState(1)

  // Dialog state
  const [selectedFile, setSelectedFile] = useState<AdventureMediaFileDto | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [usagesOpen, setUsagesOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [fileToDelete, setFileToDelete] = useState<AdventureMediaFileDto | null>(null)

  // Upload state
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number
    mouseY: number
    file: AdventureMediaFileDto
  } | null>(null)

  // Build query params
  const queryParams = useMemo<GetAdventureFilesParams>(() => {
    const params: GetAdventureFilesParams = {
      page,
      pageSize: PAGE_SIZE,
      sortBy,
      sortDesc,
    }

    if (searchTerm) params.searchTerm = searchTerm
    if (mediaType !== 'all') params.mediaType = mediaType

    return params
  }, [page, sortBy, sortDesc, searchTerm, mediaType])

  // Data fetching
  const { data: filesData, isLoading, error } = useAdventureFiles(adventureId, queryParams)
  const uploadFile = useUploadAdventureFile(adventureId)
  const deleteFile = useDeleteAdventureFile(adventureId)

  const files = filesData?.files ?? []
  const totalPages = filesData?.totalPages ?? 1

  // Upload handlers
  const handleFiles = useCallback(async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0 || !canModify) return

    const filesToUpload = Array.from(fileList)
    setUploadProgress({ current: 0, total: filesToUpload.length })

    for (let i = 0; i < filesToUpload.length; i++) {
      try {
        await uploadFile.mutateAsync({ file: filesToUpload[i] })
        setUploadProgress({ current: i + 1, total: filesToUpload.length })
      } catch (error) {
        console.error('Failed to upload file:', filesToUpload[i].name, error)
      }
    }

    // Reset after a short delay to show completion
    setTimeout(() => {
      setUploadProgress(null)
    }, 1000)
  }, [uploadFile, canModify])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (canModify) {
      setIsDragOver(true)
    }
  }, [canModify])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    if (canModify) {
      handleFiles(e.dataTransfer.files)
    }
  }, [handleFiles, canModify])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files)
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [handleFiles])

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  // File handlers
  const handleFileClick = (file: AdventureMediaFileDto) => {
    setSelectedFile(file)
    setDetailsOpen(true)
  }

  const handleContextMenuOpen = (event: React.MouseEvent<HTMLElement>, file: AdventureMediaFileDto) => {
    event.preventDefault()
    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      file,
    })
  }

  const handleContextMenuClose = () => {
    setContextMenu(null)
  }

  const handleViewDetails = () => {
    if (contextMenu) {
      setSelectedFile(contextMenu.file)
      setDetailsOpen(true)
      handleContextMenuClose()
    }
  }

  const handleViewUsages = () => {
    if (contextMenu) {
      setSelectedFile(contextMenu.file)
      setUsagesOpen(true)
      handleContextMenuClose()
    }
  }

  const handleCopyUrl = async () => {
    if (contextMenu) {
      await navigator.clipboard.writeText(contextMenu.file.fileUrl)
      handleContextMenuClose()
    }
  }

  const handleDeleteClick = () => {
    if (contextMenu) {
      setFileToDelete(contextMenu.file)
      setDeleteConfirmOpen(true)
      handleContextMenuClose()
    }
  }

  const handleDeleteFromDetails = () => {
    if (selectedFile) {
      setFileToDelete(selectedFile)
      setDeleteConfirmOpen(true)
    }
  }

  const handleDeleteConfirm = async () => {
    if (fileToDelete) {
      try {
        await deleteFile.mutateAsync(fileToDelete.id)
        setDeleteConfirmOpen(false)
        setFileToDelete(null)
        // Close details dialog if deleting the currently viewed file
        if (selectedFile?.id === fileToDelete.id) {
          setDetailsOpen(false)
          setSelectedFile(null)
        }
      } catch (error) {
        console.error('Failed to delete file:', error)
      }
    }
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
    setPage(1)
  }

  const isUploading = uploadFile.isPending || uploadProgress !== null

  // No adventure ID warning
  if (!adventureId) {
    return (
      <Alert severity="warning">
        Please save the adventure first before managing files.
      </Alert>
    )
  }

  return (
    <Box>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={handleFileInputChange}
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
      />

      {/* Upload Progress */}
      {uploadProgress && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box sx={{ width: '100%' }}>
            <Typography variant="body2" gutterBottom>
              Uploading files... ({uploadProgress.current} / {uploadProgress.total})
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(uploadProgress.current / uploadProgress.total) * 100}
            />
          </Box>
        </Alert>
      )}

      {/* Published Read-Only Warning */}
      {isPublished && !canModify && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Files are read-only because this adventure is published. Only system moderators can modify files.
        </Alert>
      )}

      {/* Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          {/* Upload button */}
          <Button
            variant="contained"
            size="small"
            startIcon={<Add fontSize="small" />}
            onClick={handleUploadClick}
            disabled={isUploading || !canModify}
          >
            Upload
          </Button>

          {/* Search */}
          <TextField
            placeholder="Search files..."
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ width: 180 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              sx: { height: 32 },
            }}
          />

          {/* Media type filter */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={mediaType}
              displayEmpty
              onChange={(e) => {
                setMediaType(e.target.value as MediaTypeFilter)
                setPage(1)
              }}
              sx={{ height: 32, fontSize: '0.875rem' }}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="image">Images</MenuItem>
              <MenuItem value="video">Videos</MenuItem>
              <MenuItem value="audio">Audio</MenuItem>
              <MenuItem value="document">Documents</MenuItem>
            </Select>
          </FormControl>

          {/* Sort */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value as SortBy)
                setPage(1)
              }}
              sx={{ height: 32, fontSize: '0.875rem' }}
            >
              <MenuItem value="date">Date</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="size">Size</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ flex: 1 }} />

          {/* View mode toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, value) => value && setViewMode(value)}
            size="small"
          >
            <ToggleButton value="grid" sx={{ p: 0.5 }}>
              <GridView fontSize="small" />
            </ToggleButton>
            <ToggleButton value="list" sx={{ p: 0.5 }}>
              <ViewList fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      {/* Loading state */}
      {isLoading && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 2,
          }}
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <AdventureFileCardSkeleton key={i} />
          ))}
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load files. Please try again later.
        </Alert>
      )}

      {/* Empty state - Drag & Drop Zone */}
      {!isLoading && !error && files.length === 0 && (
        <Paper
          elevation={0}
          onClick={canModify ? handleUploadClick : undefined}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            p: 6,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: isDragOver ? 'primary.main' : 'divider',
            bgcolor: isDragOver ? 'primary.light' : 'transparent',
            cursor: canModify ? 'pointer' : 'default',
            transition: 'all 0.2s ease',
            '&:hover': canModify
              ? {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                }
              : {},
          }}
        >
          <CloudUpload
            sx={{
              fontSize: 64,
              color: isDragOver ? 'primary.main' : 'text.secondary',
              mb: 2,
            }}
          />
          <Typography variant="h6" color={isDragOver ? 'primary.main' : 'text.secondary'} gutterBottom>
            {isDragOver ? 'Drop files here' : 'No Files Found'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm
              ? 'No files match your search criteria.'
              : canModify
              ? 'Drag and drop files here, or click to browse'
              : 'No files have been uploaded to this adventure yet.'}
          </Typography>
          {!searchTerm && canModify && (
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={(e) => {
                e.stopPropagation()
                handleUploadClick()
              }}
              disabled={isUploading}
            >
              Select Files
            </Button>
          )}
        </Paper>
      )}

      {/* Grid View */}
      {!isLoading && !error && files.length > 0 && viewMode === 'grid' && (
        <>
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
              },
              gap: 2,
              p: isDragOver ? 2 : 0,
              border: isDragOver ? '2px dashed' : 'none',
              borderColor: 'primary.main',
              borderRadius: 2,
              bgcolor: isDragOver ? 'primary.light' : 'transparent',
              transition: 'all 0.2s ease',
            }}
          >
            {files.map((file) => (
              <AdventureFileCard
                key={file.id}
                file={file}
                onClick={() => handleFileClick(file)}
                onMenuClick={(e) => handleContextMenuOpen(e, file)}
              />
            ))}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* List View */}
      {!isLoading && !error && files.length > 0 && viewMode === 'list' && (
        <>
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              p: isDragOver ? 2 : 0,
              border: isDragOver ? '2px dashed' : 'none',
              borderColor: 'primary.main',
              borderRadius: 2,
              bgcolor: isDragOver ? 'primary.light' : 'transparent',
              transition: 'all 0.2s ease',
            }}
          >
            <AdventureFileListView
              files={files}
              onFileClick={handleFileClick}
              onMenuClick={handleContextMenuOpen}
            />
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Drag overlay when files exist */}
      {isDragOver && files.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6">Drop files to upload</Typography>
          </Paper>
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleViewDetails}>
          <ListItemIcon>
            <Info fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleViewUsages}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Usages</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopyUrl}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy URL</ListItemText>
        </MenuItem>
        {canModify && (
          <MenuItem onClick={handleDeleteClick}>
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* File Details Dialog */}
      <AdventureFileDetailsDialog
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false)
          setSelectedFile(null)
        }}
        file={selectedFile}
        adventureId={adventureId}
        onDelete={canModify ? handleDeleteFromDetails : undefined}
        canEdit={canModify}
      />

      {/* File Usages Dialog */}
      <AdventureFileUsagesDialog
        open={usagesOpen}
        onClose={() => {
          setUsagesOpen(false)
          setSelectedFile(null)
        }}
        adventureId={adventureId}
        fileId={selectedFile?.id || null}
        fileName={selectedFile?.displayName || selectedFile?.fileName}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false)
          setFileToDelete(null)
        }}
      >
        <DialogTitle>Delete File?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            <strong>
              {fileToDelete?.displayName || fileToDelete?.fileName}
            </strong>
            ? This action cannot be undone.
          </DialogContentText>
          {(fileToDelete?.usageCount ?? 0) > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This file is currently used in {fileToDelete?.usageCount} place
              {fileToDelete?.usageCount !== 1 ? 's' : ''} within the adventure.
              Deleting it may break references.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteConfirmOpen(false)
              setFileToDelete(null)
            }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteFile.isPending}
          >
            {deleteFile.isPending ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
